<template>
  <div>
    <header-component />
    <div class="container-fluid mt-4">
      <div class="card">
        <div class="card-header">
          <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
            <h3 class="mb-0">
              Requirement List
              <span v-if="meta && meta.total" class="badge badge-danger ml-2" style="font-size:0.75rem;">
                {{ meta.total }} item{{ meta.total !== 1 ? 's' : '' }} below minimum
              </span>
            </h3>
            <div class="d-flex flex-column align-items-end gap-2">
              <select v-model="selectedBranchId" @change="onBranchChange" class="form-control form-control-sm" style="width:200px;">
                <option value="">Select Branch</option>
                <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
              </select>
              <div class="d-flex align-items-center gap-2">
                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  :disabled="selectedCount === 0 || creating"
                  @click="openCreateOrder"
                >
                  Create Order
                </button>
              </div>
            </div>
          </div>
          <div class="d-flex flex-wrap gap-2 align-items-center">
            <input
              v-model="search"
              @input="debouncedFetch"
              type="text"
              placeholder="Search product..."
              class="form-control form-control-sm"
              style="width:160px;"
            />
            <el-select v-model="company" @change="onFilterChange" filterable clearable placeholder="Company" size="small" style="width:160px;">
              <el-option v-for="v in companyOptions" :key="v" :label="v" :value="v" />
            </el-select>
            <el-select v-model="brand" @change="onFilterChange" filterable clearable placeholder="Brand" size="small" style="width:150px;">
              <el-option v-for="v in brandOptions" :key="v" :label="v" :value="v" />
            </el-select>
            <el-select v-model="supplier" @change="onFilterChange" filterable clearable placeholder="Supplier" size="small" style="width:170px;">
              <el-option v-for="s in supplierOptions" :key="s.supplierId" :label="s.name" :value="s.name" />
            </el-select>
            <button v-if="hasFilters" @click="clearFilters" class="btn btn-sm btn-outline-secondary">Clear</button>
          </div>
        </div>

        <div v-if="!selectedBranchId" class="p-4 text-center text-muted">
          Select a branch to view the order list.
        </div>

        <div v-else>
          <div
            class="table-nav-wrapper requirement-table-wrap"
            tabindex="0"
            @keydown="handleTableKeydown"
            @blur="focusedRowIndex = -1"
          >
          <table class="table table-sm table-flush table-striped requirement-table">
            <colgroup>
              <col class="col-check" />
              <col class="col-product" />
              <col class="col-branch" />
              <col class="col-meta" />
              <col class="col-meta" />
              <col class="col-meta" />
              <col class="col-meta" />
              <col class="col-qty" />
              <col class="col-qty col-qty-min" />
              <col class="col-qty" />
            </colgroup>
            <thead class="thead-light">
              <tr>
                <th class="col-check">
                  <input
                    type="checkbox"
                    :checked="allCurrentSelected"
                    :disabled="!items.length || loading"
                    aria-label="Select all"
                    @change="toggleSelectAll"
                  />
                </th>
                <th class="col-product">Product</th>
                <th class="col-branch">Branch</th>
                <th class="col-meta">Barcode</th>
                <th class="col-meta">Company</th>
                <th class="col-meta">Brand</th>
                <th class="col-meta">Supplier</th>
                <th class="col-qty">Current Qty</th>
                <th class="col-qty col-qty-min">Min Qty</th>
                <th class="col-qty">Order Qty</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="10" class="text-center py-3">Loading...</td>
              </tr>
              <tr v-else-if="!items.length">
                <td colspan="10" class="text-center text-muted py-3">
                  {{ selectedBranchId ? 'All products are at or above minimum quantity.' : '' }}
                </td>
              </tr>
              <tr
                v-for="(row, idx) in items"
                :key="row.product_id"
                :class="{ 'row-focused': idx === focusedRowIndex }"
                @mouseenter="focusedRowIndex = idx"
                @mouseleave="focusedRowIndex = -1"
              >
                <td class="col-check">
                  <input
                    type="checkbox"
                    :checked="isSelected(row)"
                    :aria-label="'Select ' + row.product_name"
                    @change="toggleRow(row)"
                    @click.stop
                  />
                </td>
                <td class="col-product" :title="row.product_name">
                  <span class="font-weight-bold cell-clip">{{ row.product_name }}</span>
                </td>
                <td class="col-branch">
                  <span
                    v-if="selectedBranch"
                    class="branch-badge"
                    :title="selectedBranch.name"
                    :style="branchBadgeStyle(selectedBranch.color)"
                  >{{ selectedBranch.name }}</span>
                  <span v-else>—</span>
                </td>
                <td class="col-meta text-muted" :title="row.barcode || ''">{{ row.barcode || '—' }}</td>
                <td class="col-meta text-muted" :title="row.company || ''">{{ row.company || '—' }}</td>
                <td class="col-meta text-muted" :title="row.brand || ''">{{ row.brand || '—' }}</td>
                <td class="col-meta text-muted" :title="row.supplier_name || ''">{{ row.supplier_name || '—' }}</td>
                <td class="col-qty" :title="String(Math.floor(row.quantity))">
                  <span class="text-danger font-weight-bold">{{ Math.floor(row.quantity) }}</span>
                </td>
                <td class="col-qty col-qty-min text-muted" :title="String(row.minimum_quantity)">{{ row.minimum_quantity }}</td>
                <td class="col-qty" :title="String(displayOrderQty(row))">
                  <span class="text-primary font-weight-bold">
                    {{ displayOrderQty(row) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
          </div>
          <div v-if="meta" class="card-footer">
            <div class="flex-wrap col-12 d-flex justify-content-center justify-content-sm-between">
              <p class="card-category">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</p>
              <pagination :meta="meta" @paginate="onPaginate" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <modal :show.sync="createModalShow" size="lg" @close="clearCreateOrder">
      <template #header>
        <h3 class="modal-title mb-0">Create Order</h3>
      </template>

      <p v-if="draftSupplierLabel" class="text-muted mb-2">{{ draftSupplierLabel }}</p>
      <p v-if="draftSupplierConflict" class="text-danger mb-2">
        Selected products belong to more than one supplier. Remove products until only one supplier remains.
      </p>
      <div class="table-responsive">
        <table class="table table-sm mb-0">
          <thead class="thead-light">
            <tr>
              <th>Product</th>
              <th style="width:140px;">Quantity</th>
              <th style="width:70px;"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!draftItems.length">
              <td colspan="3" class="text-center text-muted">No products selected.</td>
            </tr>
            <tr v-for="(item, index) in draftItems" :key="item.product_id">
              <td>{{ item.product_name || '—' }}</td>
              <td>
                <input
                  v-model="item.quantity"
                  type="number"
                  min="1"
                  step="1"
                  class="form-control form-control-sm"
                  :class="{ 'is-invalid': !isValidDraftQuantity(item.quantity) }"
                  :aria-label="'Quantity for ' + item.product_name"
                />
              </td>
              <td class="text-right">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  @click="removeDraftItem(index)"
                >
                  Remove
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <template #footer>
        <button type="button" class="btn btn-secondary" :disabled="creating" @click="closeCreateOrder">Close</button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="creating || !draftItems.length"
          @click="confirmCreateOrder"
        >
          {{ creating ? 'Creating...' : 'Create Order' }}
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import headerComponent from "../Components/HeaderComponent";
import pagination from "@/components/AmsaPagination";
import Modal from "@/components/Modal.vue";
import { apiHelper } from "@/utils/helpers";
import endpoints from "@/utils/constants";
import { Select, Option } from "element-ui";

const LARAVEL = endpoints.LARAVEL_BASE_URL;
const NODE    = endpoints.BASE_URL;

export default {
  components: {
    "header-component": headerComponent,
    pagination,
    modal: Modal,
    [Select.name]: Select,
    [Option.name]: Option,
  },
  data() {
    return {
      branches: [],
      selectedBranchId: "",
      search: "",
      company: "",
      brand: "",
      supplier: "",
      companyOptions: [],
      brandOptions: [],
      supplierOptions: [],
      items: [],
      meta: null,
      page: 1,
      loading: false,
      creating: false,
      createModalShow: false,
      draftItems: [],
      selected: {},
      _fetchTimer: null,
      focusedRowIndex: -1,
    };
  },
  computed: {
    hasFilters() {
      return this.search || this.company || this.brand || this.supplier;
    },
    selectedCount() {
      return Object.keys(this.selected).length;
    },
    allCurrentSelected() {
      return this.items.length > 0 && this.items.every(row => !!this.selected[row.product_id]);
    },
    selectedBranch() {
      return this.branches.find(branch => String(branch.id) === String(this.selectedBranchId)) || null;
    },
    draftSupplierIds() {
      return [...new Set(this.draftItems.map(row => row.supplier_id).filter(Boolean))];
    },
    draftSupplierLabel() {
      const names = [...new Set(this.draftItems.map(row => (row.supplier_name || "").trim()).filter(Boolean))];
      return names.length === 1 ? names[0] : "";
    },
    draftSupplierConflict() {
      return this.draftSupplierIds.length > 1;
    },
  },
  mounted() {
    apiHelper.call("get", LARAVEL + "/api/v1/branches")
      .then(res => { this.branches = res.data.data || res.data; })
      .catch(() => {});

    apiHelper.call("get", LARAVEL + "/api/v1/branch-inventory/attribute-values")
      .then(res => {
        this.companyOptions = res.data.company || [];
        this.brandOptions   = res.data.brand   || [];
      })
      .catch(() => {});

    apiHelper.call("get", NODE + endpoints.API.suppliers)
      .then(res => { this.supplierOptions = res.data || []; })
      .catch(() => {});

    apiHelper.call("get", LARAVEL + "/api/v1/me")
      .then(res => {
        if (res.data && res.data.current_branch_id) {
          this.selectedBranchId = res.data.current_branch_id;
          this.fetchOrderList();
        }
      })
      .catch(() => {});
  },
  methods: {
    onBranchChange() {
      this.page = 1;
      this.selected = {};
      this.fetchOrderList();
    },
    hasPrice(item) {
      return item.unit_price !== null && item.unit_price !== "" && Number.isFinite(Number(item.unit_price));
    },
    isValidDraftQuantity(value) {
      const quantity = Number(value);
      return Number.isFinite(quantity) && quantity > 0;
    },
    normalizedQuantity(value) {
      return Math.round(Number(value) * 1000) / 1000;
    },
    openCreateOrder() {
      if (this.creating || this.selectedCount === 0) return;
      this.draftItems = Object.values(this.selected).map(row => ({
        product_id: row.product_id,
        product_name: row.product_name,
        supplier_id: this.supplierIdFor(row),
        supplier_name: row.supplier_name || "",
        unit_price: row.purchase_price,
        quantity: this.displayOrderQty(row),
      }));
      this.createModalShow = true;
    },
    closeCreateOrder() {
      if (this.creating) return;
      this.createModalShow = false;
      this.clearCreateOrder();
    },
    clearCreateOrder() {
      if (this.creating) return;
      this.draftItems = [];
    },
    removeDraftItem(index) {
      this.draftItems.splice(index, 1);
    },
    branchBadgeStyle(color) {
      if (!color) return { background: "#dbeafe", color: "#1e40af" };
      return { background: color, color: "#fff" };
    },
    displayOrderQty(row) {
      const configured = Number(row.order_quantity);
      if (row.order_quantity != null && row.order_quantity !== "" && configured > 0) {
        return configured;
      }
      const needed = Number(row.minimum_quantity || 0) - Number(row.quantity || 0);
      return needed > 0 ? needed : 0;
    },
    supplierIdFor(row) {
      if (row.supplier_id) return row.supplier_id;
      const name = (row.supplier_name || "").trim().toLowerCase();
      if (!name) return null;
      const match = this.supplierOptions.find(
        supplier => (supplier.name || "").trim().toLowerCase() === name
      );
      return match ? match.supplierId || match.supplier_id : null;
    },
    isSelected(row) {
      return !!this.selected[row.product_id];
    },
    toggleRow(row) {
      if (this.selected[row.product_id]) {
        this.$delete(this.selected, row.product_id);
        return;
      }
      this.$set(this.selected, row.product_id, Object.assign({}, row));
    },
    toggleSelectAll(event) {
      if (event.target.checked) {
        this.items.forEach(row => {
          this.$set(this.selected, row.product_id, Object.assign({}, row));
        });
        return;
      }
      this.items.forEach(row => {
        this.$delete(this.selected, row.product_id);
      });
    },
    notify(message, type) {
      if (window.notify) {
        window.notify({ message, type, title: type === "success" ? "Success" : "Error" });
      }
    },
    orderErrorMessage(err) {
      const data = err && err.response && err.response.data;
      if (data && data.errors) {
        const first = Object.values(data.errors)[0];
        if (Array.isArray(first) && first.length) return first[0];
        if (typeof first === "string") return first;
      }
      if (data && data.message) return data.message;
      return "Failed to create order.";
    },
    confirmCreateOrder() {
      if (this.creating) return;
      const rows = this.draftItems.map(row => ({
        product_id: row.product_id,
        product_name: row.product_name,
        supplier_id: row.supplier_id,
        unit_price: row.unit_price,
        quantity: this.normalizedQuantity(row.quantity),
      }));
      if (!rows.length) {
        this.notify("Select at least one product.", "danger");
        return;
      }
      if (rows.some(row => !this.isValidDraftQuantity(row.quantity))) {
        this.notify("Each product needs a quantity greater than zero.", "danger");
        return;
      }
      const missingPrice = rows.find(row => !this.hasPrice(row));
      if (missingPrice) {
        this.notify((missingPrice.product_name || "A product") + " has no purchase price.", "danger");
        return;
      }
      if (rows.some(row => !row.supplier_id)) {
        this.notify("Selected products have no supplier.", "danger");
        return;
      }
      const supplierIds = [...new Set(rows.map(row => row.supplier_id))];
      if (supplierIds.length !== 1) {
        this.notify("Selected products belong to more than one supplier.", "danger");
        return;
      }
      const items = rows.map(row => ({
        product_id: row.product_id,
        quantity: row.quantity,
      }));
      this.creating = true;
      apiHelper.call("post", LARAVEL + "/api/v1/orders", {
        supplier_id: supplierIds[0],
        branch_id: this.selectedBranchId,
        items,
      })
        .then(() => {
          this.notify("Order created.", "success");
          this.selected = {};
          this.draftItems = [];
          this.createModalShow = false;
          this.$router.push({
            name: "CreatedOrders",
            query: { branch_id: this.selectedBranchId },
          });
        })
        .catch(err => {
          const status = err.response && err.response.status;
          if (status === 401 || status === 503) return;
          this.notify(this.orderErrorMessage(err), "danger");
        })
        .finally(() => { this.creating = false; });
    },
    onFilterChange() {
      this.page = 1;
      this.selected = {};
      this.fetchOrderList();
    },
    debouncedFetch() {
      clearTimeout(this._fetchTimer);
      this._fetchTimer = setTimeout(() => {
        this.page = 1;
        this.selected = {};
        this.fetchOrderList();
      }, 350);
    },
    clearFilters() {
      this.search   = "";
      this.company  = "";
      this.brand    = "";
      this.supplier = "";
      this.page     = 1;
      this.selected = {};
      this.fetchOrderList();
    },
    onPaginate(data) {
      this.page = data.page;
      this.fetchOrderList();
    },
    handleTableKeydown(e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.focusedRowIndex = Math.min(this.focusedRowIndex + 1, this.items.length - 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.focusedRowIndex = Math.max(this.focusedRowIndex - 1, 0);
      }
    },
    fetchOrderList() {
      if (!this.selectedBranchId) return;
      this.loading = true;
      var p = new URLSearchParams({ branch_id: this.selectedBranchId, page: this.page, per_page: 50 });
      if (this.search)   p.append("search",   this.search);
      if (this.company)  p.append("company",  this.company);
      if (this.brand)    p.append("brand",    this.brand);
      if (this.supplier) p.append("supplier", this.supplier);
      apiHelper.call("get", LARAVEL + "/api/v1/branch-inventory/order-list?" + p.toString())
        .then(res => {
          this.items = res.data.items || [];
          this.meta  = res.data.meta  || null;
          this.items.forEach(row => {
            if (this.selected[row.product_id]) {
              this.$set(this.selected, row.product_id, Object.assign({}, row));
            }
          });
        })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },
  },
};
</script>

<style scoped>
.table-nav-wrapper { outline: none; }

.requirement-table-wrap {
  max-width: 100%;
  overflow-x: auto;
}

.card .requirement-table {
  width: 100%;
  min-width: 720px;
  table-layout: fixed;
  margin-bottom: 0;
}

.card .requirement-table th,
.card .requirement-table td {
  padding-left: 0.45rem;
  padding-right: 0.45rem;
  vertical-align: middle;
  overflow: hidden;
}

.requirement-table .col-check { width: 2.25rem; }
.requirement-table .col-qty { width: 8rem; }
.requirement-table .col-qty-min { width: 6.5rem; }

.card .requirement-table th.col-qty,
.card .requirement-table td.col-qty {
  white-space: nowrap;
  text-align: right;
  padding-left: 0.35rem;
  padding-right: 0.55rem;
}

.card .requirement-table td.col-qty {
  letter-spacing: 0;
}

.card .requirement-table th.col-product,
.card .requirement-table td.col-product,
.card .requirement-table th.col-branch,
.card .requirement-table td.col-branch,
.card .requirement-table th.col-meta,
.card .requirement-table td.col-meta {
  white-space: nowrap;
  text-overflow: ellipsis;
}

.requirement-table .cell-clip,
.requirement-table .branch-badge {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.requirement-table .branch-badge {
  padding: 0.1rem 0.45rem;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.3;
  border-radius: 999px;
}

.table tbody tr {
  transition: background-color 0.12s ease, box-shadow 0.12s ease;
}

.table tbody tr:hover td,
.table tbody tr.row-focused td {
  background-color: rgba(94, 114, 228, 0.1) !important;
}

.table tbody tr.row-focused td {
  background-color: rgba(94, 114, 228, 0.17) !important;
}

.table tbody tr.row-focused td:nth-child(2) {
  box-shadow: inset 3px 0 0 #5e72e4;
}
</style>
